/**
 * JournalEntriesTab — Phase 6a.15 per Frontend Spec §21.8.4.
 *
 * The 4th results tab. Surfaces `aggregations.aggregatedJournalEntries`
 * in the canonical Big4 audit "yevmiye defteri" format used by
 * Coca-Cola İçecek 2025, Ford Otomotiv 2025, Turkcell 2025 auditor
 * working papers.
 *
 * Layout:
 *
 *   • Sticky SummaryCard at top — total counts, filtered Dr/Cr,
 *     reconciliation flag. Always visible while scrolling.
 *   • FilterBar — Period (Audit Period / All Periods) × Entry Type
 *     × Contract. All client-side, composed with AND logic.
 *   • Monthly groups in ascending chronological order. Header carries
 *     the Turkish month name ("Ocak 2025") + entry count.
 *   • One JE card per entry: header line (JE-NNN · period · type ·
 *     contractCount + ids), Dr/Cr table with every line rendered, and
 *     a TOPLAM row at the bottom with the Big4 balance check (✓/⚠).
 *
 * Account name TR mapping: backend emits English account names
 * ("Right-of-Use Asset — Buildings", "Lease Liability"). The frontend
 * maps to TR via a small per-class composition helper — split on the
 * em-dash, look up the base account label, look up the class label.
 * Unknown account strings fall through to the raw English string —
 * defensive, keeps debugging possible if the backend introduces a new
 * account.
 *
 * Per-contract drill-down is DEFERRED to Phase 6a.18 (unified drill-down
 * across all tabs).
 */

import { format, isWithinInterval, parseISO } from "date-fns";
import { Info } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";

import { JournalYearlySummary } from "@/components/journal/JournalYearlySummary";
import { PostingGuidancePanel } from "@/features/engagement/components/PostingGuidancePanel";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatCurrency } from "@/lib/format";
import { cn } from "@/lib/utils";
import type {
  AggregatedJournalEntry,
  EngagementHistoryEntry,
  JournalEntryType,
} from "@/types";

import {
  MonthGroup,
  sumDrCr,
} from "./journalEntryComponents";

// Phase 6a.18 — re-export shared helpers + types so the existing
// `JournalEntriesTab.test.tsx` imports (mapAccountToTr, consolidateJEs,
// categorizeAccount) keep resolving against this module without a
// test-side rewrite.
export {
  ACCOUNT_CATEGORY_ORDER,
  categorizeAccount,
  consolidateJEs,
  mapAccountToTr,
  type AccountCategory,
  type AccountConsolidation,
  type ConsolidatedSummary,
} from "./journalEntryComponents";

interface OutletContext {
  engagement: EngagementHistoryEntry;
}

type PeriodFilter = "AUDIT_PERIOD" | "ALL_PERIODS";

const ALL_JE_TYPES: JournalEntryType[] = [
  "INITIAL_RECOGNITION",
  "MONTHLY_INTEREST",
  "MONTHLY_DEPRECIATION",
  "LEASE_PAYMENT",
  "LEASE_MODIFICATION_INCREASE",
  // BB3 — backend Phase 6c.1b emits this as a distinct type for
  // scope-increase modifications failing the IFRS 16 §45 "separate
  // lease" test. Previously absent from the filter, so backend
  // entries with this type were silently hidden.
  "LEASE_MODIFICATION_INCREASE_NOT_SEPARATE",
  "LEASE_MODIFICATION_DECREASE_STEP1",
  "LEASE_MODIFICATION_DECREASE_STEP2",
  "LEASE_TERMINATION",
  "INDEX_REMEASUREMENT",
  "LEASE_RECLASSIFICATION",
  "FX_REMEASUREMENT",
  // BB3 — backend emits period-end IAS 21 revaluation rows under
  // this distinct type. Missing here is what kept FX revaluation
  // entries from appearing in the engagement-level Yevmiye list.
  "FX_REVALUATION",
  "VARIABLE_PAYMENT",
  // Bulgu 4 — AUDIT_ADJUSTMENT (prior-year reconciliation PAJEs) is excluded
  // from the consolidated journal by the backend (it would double-count the
  // reconciliation). It is NOT a filterable type here; PAJEs live on the PAJE
  // tab, and the per-contract drill-down (ContractDetailPage) still shows it.
];

export function JournalEntriesTab() {
  const { t } = useTranslation();
  const { engagement } = useOutletContext<OutletContext>();
  const aggs = engagement.lastResult?.aggregations;
  const jes = aggs?.aggregatedJournalEntries ?? [];
  const currency = engagement.functionalCurrency;
  const auditPeriodStart = engagement.auditPeriodStart;
  const auditPeriodEnd = engagement.auditPeriodEnd;

  const allContractIds = useMemo(
    () => Array.from(new Set(jes.flatMap((je) => je.contractIds))).sort(),
    [jes],
  );

  const [periodFilter, setPeriodFilter] =
    useState<PeriodFilter>("AUDIT_PERIOD");
  const [selectedTypes, setSelectedTypes] = useState<Set<JournalEntryType>>(
    () => new Set(ALL_JE_TYPES),
  );
  // Contract selection is initialised on first render to include every
  // contract present in the JE list. We deliberately do NOT add later
  // arrivals automatically — the user's deselection should persist
  // across renders.
  const [selectedContracts, setSelectedContracts] = useState<Set<string>>(
    () => new Set(allContractIds),
  );

  const filtered = useMemo(() => {
    if (jes.length === 0) return [];
    const periodStart = parseISO(auditPeriodStart);
    const periodEnd = parseISO(auditPeriodEnd);
    return jes.filter((je) => {
      if (periodFilter === "AUDIT_PERIOD") {
        const periodDate = parseISO(je.period);
        if (
          !isWithinInterval(periodDate, {
            start: periodStart,
            end: periodEnd,
          })
        ) {
          return false;
        }
      }
      if (!selectedTypes.has(je.type)) return false;
      if (selectedContracts.size === 0) return false;
      if (!je.contractIds.some((c) => selectedContracts.has(c))) return false;
      return true;
    });
  }, [
    jes,
    periodFilter,
    selectedTypes,
    selectedContracts,
    auditPeriodStart,
    auditPeriodEnd,
  ]);

  const { totalDebit, totalCredit } = useMemo(
    () => sumDrCr(filtered),
    [filtered],
  );
  const reconciles =
    Number.isFinite(totalDebit - totalCredit) &&
    Math.abs(totalDebit - totalCredit) < 0.01;

  const groupedByMonth = useMemo(() => {
    const groups = new Map<string, AggregatedJournalEntry[]>();
    for (const je of filtered) {
      const key = format(parseISO(je.period), "yyyy-MM");
      const bucket = groups.get(key);
      if (bucket) bucket.push(je);
      else groups.set(key, [je]);
    }
    return Array.from(groups.entries()).sort(([a], [b]) =>
      a.localeCompare(b),
    );
  }, [filtered]);

  // Phase 6a.15a — annual consolidation across ALL filtered JEs is
  // pinned to the top of the tab as the auditor's "forest" view. The
  // panel chrome + aggregation now live in the shared
  // `JournalYearlySummary` component (BB3) so the per-contract Yevmiye
  // section can mount the same panel without duplicating UI state.

  if (jes.length === 0) {
    return (
      <Card className="p-8" data-testid="je-empty">
        <p className="text-sm italic text-muted-foreground">
          {t("je.emptyState")}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* "Yevmiye mi, PAJE mi?" — shared posting-guidance explainer so the
          auditor knows the full ledger here is the IFRS 16 booking (vs the
          PAJE tab's adjustments), and that the two are not posted together. */}
      <PostingGuidancePanel id="je-posting" />

      <SummaryCard
        totalAll={jes.length}
        totalFiltered={filtered.length}
        totalDebit={totalDebit}
        totalCredit={totalCredit}
        reconciles={reconciles}
        currency={currency}
      />

      <FilterBar
        periodFilter={periodFilter}
        onPeriodFilterChange={setPeriodFilter}
        auditPeriodStart={auditPeriodStart}
        selectedTypes={selectedTypes}
        onTypesChange={setSelectedTypes}
        selectedContracts={selectedContracts}
        onContractsChange={setSelectedContracts}
        allContractIds={allContractIds}
      />

      <JournalYearlySummary entries={filtered} currency={currency} />

      {filtered.length === 0 ? (
        <Card className="p-8" data-testid="je-empty-filtered">
          <p className="text-sm italic text-muted-foreground text-center">
            {t("je.emptyFiltered")}
          </p>
        </Card>
      ) : (
        groupedByMonth.map(([monthKey, monthJes], groupIdx) => (
          <MonthGroup
            key={monthKey}
            monthKey={monthKey}
            jes={monthJes}
            currency={currency}
            indexOffset={countBefore(groupedByMonth, groupIdx)}
            engagementId={engagement.id}
          />
        ))
      )}
    </div>
  );
}

// ─── SummaryCard ───────────────────────────────────────────────────────────

interface SummaryCardProps {
  totalAll: number;
  totalFiltered: number;
  totalDebit: number;
  totalCredit: number;
  reconciles: boolean;
  currency: string;
}

function SummaryCard({
  totalAll,
  totalFiltered,
  totalDebit,
  totalCredit,
  reconciles,
  currency,
}: SummaryCardProps) {
  const { t } = useTranslation();
  const variance = Math.abs(totalDebit - totalCredit);
  return (
    <Card
      className="p-4 bg-muted/30 sticky top-0 z-10 backdrop-blur"
      data-testid="je-summary"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
        <h3 className="text-base font-semibold">{t("je.section.title")}</h3>
        <p className="text-sm text-muted-foreground">
          {t("je.summary.allRecords", { count: totalAll })}
          {" · "}
          <span data-testid="je-summary-shown">
            {t("je.summary.shownRecords", { count: totalFiltered })}
          </span>
        </p>
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm font-mono tabular-nums">
        <span data-testid="je-summary-debit">
          <strong className="font-sans font-semibold">
            {t("je.summary.totalDebit")}:
          </strong>{" "}
          {formatCurrency(totalDebit.toFixed(2), currency)}
        </span>
        <span data-testid="je-summary-credit">
          <strong className="font-sans font-semibold">
            {t("je.summary.totalCredit")}:
          </strong>{" "}
          {formatCurrency(totalCredit.toFixed(2), currency)}
        </span>
        {/* Phase BB5-5 — Borç=Alacak indicator promoted from inline text to
            an outlined badge alongside the summary totals. */}
        <span
          className={cn(
            "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
            reconciles
              ? "border-green-500/40 text-green-400"
              : "border-amber-500/40 text-amber-400",
          )}
          data-testid={
            reconciles ? "je-summary-balanced" : "je-summary-imbalanced"
          }
        >
          {reconciles
            ? t("je.summary.balanced")
            : t("je.summary.imbalanced", {
                variance: formatCurrency(variance.toFixed(2)),
                currency,
              })}
        </span>
      </div>
    </Card>
  );
}

// ─── FilterBar ─────────────────────────────────────────────────────────────

interface FilterBarProps {
  periodFilter: PeriodFilter;
  onPeriodFilterChange: (next: PeriodFilter) => void;
  auditPeriodStart: string;
  selectedTypes: Set<JournalEntryType>;
  onTypesChange: (next: Set<JournalEntryType>) => void;
  selectedContracts: Set<string>;
  onContractsChange: (next: Set<string>) => void;
  allContractIds: string[];
}

function FilterBar({
  periodFilter,
  onPeriodFilterChange,
  auditPeriodStart,
  selectedTypes,
  onTypesChange,
  selectedContracts,
  onContractsChange,
  allContractIds,
}: FilterBarProps) {
  const { t } = useTranslation();
  const auditYear = new Date(auditPeriodStart).getFullYear();

  function toggleType(type: JournalEntryType, checked: boolean) {
    const next = new Set(selectedTypes);
    if (checked) next.add(type);
    else next.delete(type);
    onTypesChange(next);
  }

  function toggleContract(contractId: string, checked: boolean) {
    const next = new Set(selectedContracts);
    if (checked) next.add(contractId);
    else next.delete(contractId);
    onContractsChange(next);
  }

  return (
    <Card className="p-4" data-testid="je-filter-bar">
      <div className="flex flex-wrap gap-6">
        <fieldset data-testid="je-filter-period">
          <legend className="text-sm font-medium mb-2 flex items-center gap-1.5">
            {t("je.filter.period.label")}
            <Tooltip>
              <TooltipTrigger asChild>
                <Info
                  size={14}
                  className="text-muted-foreground cursor-help"
                  data-testid="je-filter-period-info"
                  aria-label={t("je.filter.period.label")}
                />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                {t("je.filter.period.tooltip")}
              </TooltipContent>
            </Tooltip>
          </legend>
          <RadioGroup
            value={periodFilter}
            onValueChange={(v) =>
              onPeriodFilterChange(v as PeriodFilter)
            }
            className="flex gap-4"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem
                id="je-period-audit"
                value="AUDIT_PERIOD"
                data-testid="je-filter-period-audit"
              />
              <Label htmlFor="je-period-audit" className="text-sm">
                {t("je.filter.period.auditPeriod", { year: auditYear })}
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem
                id="je-period-all"
                value="ALL_PERIODS"
                data-testid="je-filter-period-all"
              />
              <Label htmlFor="je-period-all" className="text-sm">
                {t("je.filter.period.allPeriods")}
              </Label>
            </div>
          </RadioGroup>
        </fieldset>

        <fieldset
          data-testid="je-filter-type"
          className="flex-1 min-w-[280px]"
        >
          {/* Phase BB5-5 — "Tümünü seç / Kaldır" link-button on the right
              of the legend; checkboxes shrink to text-xs. */}
          <div className="flex items-center justify-between mb-2">
            <legend className="text-sm font-medium">
              {t("je.filter.type.label")}
            </legend>
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              onClick={() =>
                onTypesChange(
                  selectedTypes.size === ALL_JE_TYPES.length
                    ? new Set()
                    : new Set(ALL_JE_TYPES),
                )
              }
              data-testid="je-filter-type-toggle-all"
            >
              {selectedTypes.size === ALL_JE_TYPES.length
                ? t("je.filter.clearAll")
                : t("je.filter.selectAll")}
            </Button>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            {ALL_JE_TYPES.map((type) => (
              <label
                key={type}
                className="flex items-center gap-2 text-xs"
                data-testid={`je-filter-type-${type}`}
              >
                <Checkbox
                  checked={selectedTypes.has(type)}
                  onCheckedChange={(c) => toggleType(type, c === true)}
                />
                {t(`je.type.${type}` as never) as string}
              </label>
            ))}
          </div>
        </fieldset>

        {allContractIds.length > 0 && (
          <fieldset
            data-testid="je-filter-contract"
            className="flex-1 min-w-[200px]"
          >
            <div className="flex items-center justify-between mb-2">
              <legend className="text-sm font-medium">
                {t("je.filter.contract.label")}
              </legend>
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs"
                onClick={() =>
                  onContractsChange(
                    selectedContracts.size === allContractIds.length
                      ? new Set()
                      : new Set(allContractIds),
                  )
                }
                data-testid="je-filter-contract-toggle-all"
              >
                {selectedContracts.size === allContractIds.length
                  ? t("je.filter.clearAll")
                  : t("je.filter.selectAll")}
              </Button>
            </div>
            {/* Phase BB5-5 — scrollable so 12+ contracts don't blow up
                the layout vertically. */}
            <div className="flex flex-col gap-1.5 max-h-[120px] overflow-y-auto pr-2">
              {allContractIds.map((cid) => (
                <label
                  key={cid}
                  className="flex items-center gap-2 text-xs"
                  data-testid={`je-filter-contract-${cid}`}
                >
                  <Checkbox
                    checked={selectedContracts.has(cid)}
                    onCheckedChange={(c) =>
                      toggleContract(cid, c === true)
                    }
                  />
                  {cid}
                </label>
              ))}
            </div>
          </fieldset>
        )}
      </div>
    </Card>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

/** Cumulative JE count across the monthly groups up to (but excluding) the given index. */
function countBefore(
  groups: ReadonlyArray<readonly [string, AggregatedJournalEntry[]]>,
  upToIdx: number,
): number {
  let n = 0;
  for (let i = 0; i < upToIdx; i++) {
    n += groups[i]![1].length;
  }
  return n;
}
